import { useEffect, useMemo, useState } from 'react';
import { Btn, BtnIconOnly, IconEdit, IconPlus, IconTrash } from '../ui';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { deleteCompanyUnitOfMeasure, updateCompanyUnitOfMeasure } from '../../graphql/companyUnitOfMeasureQueries';
import { listServiceItemsByCompanyId, updateServiceItem } from '../../graphql/companyResourceQueries';
import { fetchPMConfigurationsByCompany, updatePMConfig } from '../../graphql/pmQueries';
import { useCompanyUnitsOfMeasure } from '../../hooks/useCompanyUnitsOfMeasure';
import { normalizeUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import {
    collapseEquivalentUnitsOfMeasure,
    countUnitOfMeasureUsage,
    formatUnitOfMeasureUsage,
    unitOfMeasureUsageTotal,
    unitUsesStoredMeasure,
    type UnitOfMeasureUsage,
} from '../../utils/companyUnitOfMeasureCatalog';
import UnitOfMeasureModal from './UnitOfMeasureModal';
import ChangeUnitOfMeasureModal from './ChangeUnitOfMeasureModal';
import { DeleteUnitOfMeasureModal } from './DeleteUnitOfMeasureModal';

interface UnitsOfMeasureTabProps {
    isAdmin: boolean;
    companyId: string;
}

export const UnitsOfMeasureTab = ({ isAdmin, companyId }: UnitsOfMeasureTabProps) => {
    const { companyUnits, updateUnitInDatabase, refreshEquipment } = useEquipmentContext();
    const extraValues = useMemo(
        () =>
            companyUnits
                .map((unit) => unit.unitOfMeasure)
                .filter((value): value is string => Boolean(value?.trim())),
        [companyUnits],
    );
    const { items, loading, refresh, createUnit } = useCompanyUnitsOfMeasure(companyId, extraValues);
    const [serviceItemMeasures, setServiceItemMeasures] = useState<{ id: string; unitOfMeasure?: string | null }[]>([]);
    const [schedules, setSchedules] = useState<{ id: string; unitId?: string | null; unitOfMeasure?: string | null }[]>([]);
    const [usageVersion, setUsageVersion] = useState(0);
    const sortedItems = useMemo(
        () =>
            collapseEquivalentUnitsOfMeasure(items).sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
            ),
        [items],
    );

    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState<(typeof items)[number] | null>(null);
    const [modalError, setModalError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [pendingChange, setPendingChange] = useState<{
        item: (typeof items)[number];
        name: string;
        value: string;
        affectedCount: number;
    } | null>(null);
    const [deleting, setDeleting] = useState<(typeof items)[number] | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (!companyId) return;
        let isCancelled = false;
        (async () => {
            try {
                const [serviceItems, configs] = await Promise.all([
                    listServiceItemsByCompanyId(companyId),
                    fetchPMConfigurationsByCompany(companyId),
                ]);
                if (isCancelled) return;
                setServiceItemMeasures(
                    serviceItems.map((item) => ({ id: item.id, unitOfMeasure: item.unitOfMeasure })),
                );
                setSchedules(
                    (configs ?? [])
                        .filter((config) => config?.id)
                        .map((config) => ({
                            id: config.id,
                            unitId: config.unitId,
                            unitOfMeasure: config.unitOfMeasure,
                        })),
                );
            } catch (error) {
                console.error('Failed to load unit of measure usage:', error);
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [companyId, usageVersion]);

    const emptyUsage: UnitOfMeasureUsage = { equipment: 0, schedules: 0, serviceItems: 0 };
    const usageCounts = useMemo(() => {
        const counts = new Map<string, UnitOfMeasureUsage>();
        const equipment = companyUnits.map((unit) => ({
            id: unit.id,
            unitOfMeasure: unit.unitOfMeasure,
        }));
        for (const item of items) {
            counts.set(
                item.value,
                countUnitOfMeasureUsage(item.value, {
                    equipment,
                    schedules,
                    serviceItems: serviceItemMeasures,
                }),
            );
        }
        return counts;
    }, [companyUnits, items, schedules, serviceItemMeasures]);

    const closeEditor = () => {
        setShowModal(false);
        setEditing(null);
        setModalError(null);
    };

    const applyRename = async (item: (typeof items)[number], name: string, value: string) => {
        await updateCompanyUnitOfMeasure({
            id: item.id,
            name,
            value,
        });
        if (value !== item.value) {
            const equipmentTargets = companyUnits.filter((unit) =>
                unitUsesStoredMeasure(unit.unitOfMeasure, item.value),
            );
            const serviceTargets = serviceItemMeasures.filter((row) =>
                unitUsesStoredMeasure(row.unitOfMeasure, item.value),
            );
            const scheduleTargets = schedules.filter((schedule) =>
                unitUsesStoredMeasure(schedule.unitOfMeasure, item.value),
            );
            for (const unit of equipmentTargets) {
                await updateUnitInDatabase(unit.id, { unitOfMeasure: value });
            }
            await Promise.all([
                ...serviceTargets.map((row) => updateServiceItem({ id: row.id, unitOfMeasure: value })),
                ...scheduleTargets.map((schedule) =>
                    updatePMConfig({ id: schedule.id, unitOfMeasure: value }),
                ),
            ]);
            if (equipmentTargets.length > 0) {
                await refreshEquipment();
            }
            setUsageVersion((version) => version + 1);
        }
        await refresh();
    };

    const handleSubmit = async (name: string) => {
        setModalError(null);
        if (!editing) {
            setIsSaving(true);
            try {
                await createUnit(name);
                closeEditor();
            } catch (error) {
                console.error('Failed to add unit of measure:', error);
                setModalError(error instanceof Error ? error.message : 'Failed to add unit of measure.');
            } finally {
                setIsSaving(false);
            }
            return;
        }

        const nextValue = normalizeUnitOfMeasure(name);
        const collision = items.find((item) => item.id !== editing.id && item.value === nextValue);
        if (collision && nextValue !== editing.value) {
            setModalError(`“${collision.name}” already uses this unit of measure.`);
            return;
        }

        const affectedCount = unitOfMeasureUsageTotal(usageCounts.get(editing.value) || emptyUsage);
        const slugChanged = nextValue !== editing.value;
        if (slugChanged && affectedCount > 0) {
            setPendingChange({ item: editing, name, value: nextValue, affectedCount });
            return;
        }

        setIsSaving(true);
        try {
            await applyRename(editing, name, nextValue);
            closeEditor();
        } catch (error) {
            console.error('Failed to update unit of measure:', error);
            setModalError(error instanceof Error ? error.message : 'Failed to update unit of measure.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleConfirmChange = async () => {
        if (!pendingChange) return;
        setIsSaving(true);
        try {
            await applyRename(pendingChange.item, pendingChange.name, pendingChange.value);
            setPendingChange(null);
            closeEditor();
        } catch (error) {
            console.error('Failed to change unit of measure:', error);
            setModalError(error instanceof Error ? error.message : 'Failed to change unit of measure.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleting) return;
        const usedBy = unitOfMeasureUsageTotal(usageCounts.get(deleting.value) || emptyUsage);
        if (usedBy > 0) return;
        setIsDeleting(true);
        try {
            await deleteCompanyUnitOfMeasure(deleting.id);
            setDeleting(null);
            await refresh();
        } catch (error) {
            console.error('Failed to delete unit of measure:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading && items.length === 0) {
        return (
            <div className="cr-empty-state">
                <p className="cr-empty-state-text">Loading units of measure...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="cr-toolbar">
                {isAdmin && (
                    <Btn
                        variant="primary"
                        buttonSize="sm"
                        buttonIcon={<IconPlus />}
                        buttonText="Add Unit of Measure"
                        buttonOnClick={() => {
                            setEditing(null);
                            setModalError(null);
                            setShowModal(true);
                        }}
                    />
                )}
            </div>

            {sortedItems.length === 0 ? (
                <div className="cr-empty-state">
                    <p className="cr-empty-state-text">
                        No units of measure yet. Add your first to use it on equipment.
                    </p>
                </div>
            ) : (
                <table className="cr-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Used by</th>
                            {isAdmin && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedItems.map((item) => {
                            const usage = usageCounts.get(item.value) || emptyUsage;
                            return (
                                <tr key={item.id}>
                                    <td>{item.name}</td>
                                    <td>{formatUnitOfMeasureUsage(usage)}</td>
                                    {isAdmin && (
                                        <td className="cr-table-actions">
                                            <div className="table-actions">
                                                <Btn
                                                    variant="tertiary"
                                                    buttonSize="sm"
                                                    buttonClasses="btn-icon"
                                                    buttonIcon={<IconEdit />}
                                                    ariaLabel={`Change ${item.name}`}
                                                    buttonOnClick={() => {
                                                        setEditing(item);
                                                        setModalError(null);
                                                        setShowModal(true);
                                                    }}
                                                />
                                                <BtnIconOnly
                                                    size="sm"
                                                    variant="danger"
                                                    icon={<IconTrash />}
                                                    ariaLabel={`Delete ${item.name}`}
                                                    onClick={() => setDeleting(item)}
                                                />
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            <UnitOfMeasureModal
                visible={showModal && !pendingChange}
                existing={items}
                editing={editing}
                isSaving={isSaving}
                error={modalError}
                onDismiss={closeEditor}
                onSubmit={(name) => {
                    void handleSubmit(name);
                }}
            />

            <ChangeUnitOfMeasureModal
                visible={Boolean(pendingChange)}
                oldName={pendingChange?.item.name || ''}
                newName={pendingChange?.name || ''}
                affectedCount={pendingChange?.affectedCount || 0}
                isSaving={isSaving}
                error={modalError}
                onDismiss={() => setPendingChange(null)}
                onConfirm={() => {
                    void handleConfirmChange();
                }}
            />

            <DeleteUnitOfMeasureModal
                item={deleting}
                usageLabel={
                    deleting
                        ? formatUnitOfMeasureUsage(usageCounts.get(deleting.value) || emptyUsage)
                        : 'Not used'
                }
                usedByCount={
                    deleting ? unitOfMeasureUsageTotal(usageCounts.get(deleting.value) || emptyUsage) : 0
                }
                isDeleting={isDeleting}
                onDismiss={() => setDeleting(null)}
                onConfirmDelete={() => {
                    void handleConfirmDelete();
                }}
            />
        </div>
    );
};
